import { Analytics } from '@trello/atlassian-analytics';
import { sendErrorEvent } from '@trello/error-reporting';
import { featureFlagClient } from '@trello/feature-flag-client';

import { markdown } from 'app/src/components/TrelloMarkdown';
import type { ParserOptions } from 'app/src/components/TrelloMarkdown/TrelloMarkdownWrapper';

interface MarkdownTypeCountType {
  [key: string]: number;
}

interface MarkdownAnalyzeMapType {
  description: ParserOptions;
  checkItems: ParserOptions;
  comments: ParserOptions;
}

export type MarkdownLocationType = keyof MarkdownAnalyzeMapType;

// eslint-disable-next-line @trello/enforce-variable-case
const markdownAnalyzeMap: MarkdownAnalyzeMapType = {
  description: markdown.description,
  checkItems: markdown.checkItems,
  comments: markdown.comments,
};

export const analyzeMarkdown = (
  inputValue: string,
  type: MarkdownLocationType,
): MarkdownTypeCountType | null => {
  if (!inputValue) {
    return null;
  }
  if (!featureFlagClient.get('aaaa.web.trello-markdown-tracking', false)) {
    return null;
  }
  try {
    if (markdownAnalyzeMap[type]) {
      // eslint-disable-next-line @trello/enforce-variable-case
      const markdownAnalyzeFn = markdownAnalyzeMap[type];
      const markdownData = markdownAnalyzeFn.analyze(inputValue ?? '') || [];

      const markdownTypeCount: MarkdownTypeCountType = {};
      const filteredMarkdownData = markdownData.filter((markdownInfo) => {
        const { rule } = markdownInfo;
        return rule !== 'paragraph' && rule !== 'text';
      });

      if (filteredMarkdownData.length > 0) {
        filteredMarkdownData.forEach((markdownInfo) => {
          const { rule } = markdownInfo;

          if (!Object.hasOwnProperty.call(markdownTypeCount, rule)) {
            markdownTypeCount[rule] = 0;
          }

          markdownTypeCount[rule] = markdownTypeCount[rule] + 1;
        });

        return markdownTypeCount;
      }
    }
  } catch (err) {
    sendErrorEvent(err, {
      tags: {
        ownershipArea: 'trello-aaaa',
        feature: 'Markdown Tracker',
      },
    });
  }
  return null;
};

export const markdownTracker = (
  inputValue: string,
  type: MarkdownLocationType,
) => {
  if (!inputValue) {
    return;
  }
  if (!featureFlagClient.get('aaaa.web.trello-markdown-tracking', false)) {
    return;
  }

  const start = Date.now();
  const markdownTypeCount = analyzeMarkdown(inputValue, type);

  if (markdownTypeCount) {
    Analytics.sendOperationalEvent({
      action: 'updated',
      actionSubject: 'trackMarkdown',
      source: 'lib:markdownTracker',
      attributes: {
        markdownTypeCount,
        markdownLocation: type,
      },
    });
  }

  const end = Date.now();

  Analytics.sendOperationalEvent({
    action: 'completed',
    actionSubject: 'trackMarkdown',
    source: 'lib:markdownTracker',
    attributes: {
      totalProcessTimeInMs: end - start,
    },
  });
};
